/* eslint-disable react/self-closing-comp */
import React, { useState, useEffect } from 'react';
import {
  StyleSheet,
  FlatList,
  View,
  Image,
  Platform,
  ScrollView,
  RefreshControl,
} from 'react-native';
import { SearchBar } from 'react-native-elements';
import { MainButton } from '../buttons/MainButton';
import CustomText from '../customText/CustomText';
import theme from '../../constants/theme.style.js';
import FriendRequests from '../friendRequests';
import FriendItem from '../friendItem';

const loginTurtle = require('../../assets/Turtles/main-turtle.png');

const styles = StyleSheet.create({
  container: {
    flex: 1,
    height: '100%',
  },
  noFriendsScrollView: {
    flexGrow: 1,
    justifyContent: 'flex-start',
  },
  contacts: {
    flex: 1,
    borderColor: theme.GREY_MEDIUM_COLOR,
  },
  contactsList: {
    overflow: 'hidden',
  },
  button: {
    marginTop: 80,
    textAlign: 'center',
  },
  alphabet: {
    color: theme.GREEN_WARM_COLOR,
    fontWeight: 'bold',
    paddingTop: 20,
    paddingBottom: 10,
    flex: 1,
  },
  header: {
    flexDirection: 'row-reverse',
    backgroundColor: theme.WHITE_COLOR,
  },
  friendFavorite: {
    width: 15,
    height: 15,
    marginTop: 20,
    marginRight: 5,
  },
  noMessagesContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: theme.WHITE_COLOR,
    width: '100%',
    height: 500,
    ...Platform.select({
      web: {
        height: `calc(80vh - ${theme.WEB_NAV_HEIGHT}px)`,
      },
    }),
  },
  noMessagesText: {
    marginTop: theme.CONTAINER_MARGIN,
    marginBottom: theme.CONTAINER_MARGIN * 2,
    textAlign: 'center',
    width: '80%',
  },
  turtleTop: {
    width: '30%',
    height: 120,
  },
});

function sortFriends(a, b) {
  const friendA = a.firstname.toUpperCase();
  const friendB = b.firstname.toUpperCase();

  let comparison = 0;
  if (friendA > friendB) {
    comparison = 1;
  } else if (friendA < friendB) {
    comparison = -1;
  }
  return comparison;
}

function createFriendListSections(friends) {
  const friendsSorted = friends.sort(sortFriends);
  const sectionList = [];

  friendsSorted.forEach((friend) => {
    const found = sectionList.some((el) => el.firstname === friend.firstname.substring(0, 1));
    if (!found) {
      sectionList.push({
        firstname: friend.firstname.substring(0, 1),
        header: true,
      });
    }
    // eslint-disable-next-line no-param-reassign
    friend.header = false;
    sectionList.push(friend);
  });
  return sectionList;
}

export const Friends = ({
  friends,
  selectFriend,
  onFriendsFetch,
  onFriendRequestFetch,
  currentUser,
  nav,
}) => {
  const [friendsFetched, setFriendsFetched] = useState(false);
  const [stickyHeaderIndices, setStickyHeader] = useState('Favorites');
  const [searchValue, setSearchValue] = useState('');
  const fullFriendList = createFriendListSections(friends);
  const [filteredFriendsList, setFriendsList] = useState();
  const [fetchedFriendRequests, setFetchedFriendRequests] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  if (currentUser.user === undefined) {
    nav.navigate('Auth');
    return null;
  }

  useEffect(() => {
    const arr = [];
    // eslint-disable-next-line array-callback-return
    fullFriendList.map((obj) => {
      if (obj.header) {
        arr.push(fullFriendList.indexOf(obj) + 1); // + 1 in order to skip the searchbar at index 0
      }
    });
    arr.push(0);
    setStickyHeader(arr);
    setFriendsList(fullFriendList);
  }, [friends]); // needed to wait until friendlist is passed on correctly

  if (!friendsFetched && friends.length === 0) {
    onFriendsFetch(currentUser.user.id);
    setFriendsFetched(true);
  }

  function fetchFriendsAgain(markAsFetched) {
    setFetchedFriendRequests(markAsFetched);
    onFriendsFetch(currentUser.user.id).then(() => {
      setRefreshing(false);
    });
  }

  function handleRefresh() {
    setRefreshing(true);
    fetchFriendsAgain(false);
    onFriendRequestFetch(currentUser.user.id);
  }

  if (friends.length < 1) {
    return (
      <ScrollView
        contentContainerStyle={styles.noFriendsScrollView}
        refreshControl={<RefreshControl refreshing={refreshing} onRefresh={handleRefresh} />}
      >
        <FriendRequests
          fetchFriendsAgain={fetchFriendsAgain}
          fetchedFriendRequests={fetchedFriendRequests}
        />
        <View style={styles.noMessagesContainer}>
          <Image style={styles.turtleTop} source={loginTurtle} resizeMode="contain" />
          <CustomText style={styles.noMessagesText} testID="no-messages">
            No friends yet.
            {'\n'}
            Start your journey by adding friends and writing letters to them.
          </CustomText>
          <MainButton
            title="Refresh List"
            btnBgColor={theme.GREEN_DARKER_COLOR}
            textColor="#FFFFFF"
            btnTextSize={15}
            btnWidth={200}
            btnPadding="5%"
            btnPress={() => handleRefresh()}
          />
        </View>
      </ScrollView>
    );
  }

  if (filteredFriendsList !== undefined) {
    return (
      <View style={styles.container}>
        <FlatList
          style={styles.contacts}
          contentContainerStyle={styles.contactsList}
          data={filteredFriendsList}
          showsVerticalScrollIndicator={false}
          refreshing={refreshing}
          onRefresh={handleRefresh}
          ListHeaderComponent={
            <View style={styles.listHeaderComponent}>
              <SearchBar
                round
                platform="default"
                lightTheme
                searchIcon={{ size: 24 }}
                containerStyle={{
                  backgroundColor: 'transparent',
                  borderTopWidth: 0,
                  borderBottomWidth: 0,
                  paddingLeft: 0,
                }}
                disabled={fullFriendList.length < 1}
                placeholder="Search in friend list"
                value={searchValue}
                testID="searchbar"
                onChangeText={(searchTerm) => {
                  setSearchValue(searchTerm);
                  setFriendsList(fullFriendList);
                  const searchTermEdited = searchTerm.toLowerCase().split(' ').join('');
                  if (searchTermEdited.length > 0) {
                    // eslint-disable-next-line arrow-body-style
                    const searchedList = fullFriendList.filter((friend) => {
                      if (!friend.header) {
                        let friendNames = friend.firstname + friend.lastname + friend.nickname;
                        friendNames = friendNames.toLowerCase();
                        return friendNames.includes(searchTermEdited);
                      }
                      return null;
                    });
                    if (searchedList.length < 1) {
                      searchedList.push({
                        firstname: 'No matches',
                        header: true,
                      });
                    } else {
                      searchedList.unshift({
                        firstname: 'Top name matches',
                        header: true,
                      });
                    }
                    setFriendsList(searchedList);
                  }
                }}
              />
              {!selectFriend && (
                <FriendRequests fetchFriendsAgain={fetchFriendsAgain} fetchedFriendRequests />
              )}
            </View>
          }
          stickyHeaderIndices={stickyHeaderIndices}
          keyExtractor={(item, index) => index.toString()}
          // eslint-disable-next-line consistent-return
          renderItem={({ item }) => {
            if (item.header) {
              return (
                <View>
                  <View style={styles.header}>
                    <CustomText style={styles.alphabet}>{item.firstname}</CustomText>
                  </View>
                </View>
              );
            }
            if (!item.header) {
              return <FriendItem friend={item} selectItem={selectFriend} />;
            }
          }}
          ListFooterComponent={<View style={{ width: '100%', height: 100 }} />}
        />
      </View>
    );
  }
  return null;
};

export default Friends;
